import { FC, useState, useEffect } from 'react'
import { jingqueAdminApi, AnalyzeTemplate, CreateAnalyzeTemplateRequest } from '@/api/jingqueAdminApi'

export const AnalyzeTemplatesPage: FC = () => {
  const [templates, setTemplates] = useState<AnalyzeTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<AnalyzeTemplate | null>(null)
  const [saving, setSaving] = useState(false)

  const loadTemplates = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await jingqueAdminApi.analyzeTemplates.list(page, pageSize)
      setTemplates(res.items)
      setTotal(res.total)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadTemplates() }, [page])

  const handleCreate = async (data: CreateAnalyzeTemplateRequest) => {
    setSaving(true)
    try {
      await jingqueAdminApi.analyzeTemplates.create(data)
      setShowCreate(false)
      loadTemplates()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string, data: any) => {
    setSaving(true)
    try {
      await jingqueAdminApi.analyzeTemplates.update(id, data)
      setEditing(null)
      loadTemplates()
    } catch (e: any) {
      alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('确认删除？')) return
    try {
      await jingqueAdminApi.analyzeTemplates.delete(id)
      loadTemplates()
    } catch (e: any) {
      alert(e.message)
    }
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-800">分析模板</h1>
        <div className="flex gap-2">
          <button onClick={loadTemplates} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">刷新</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg">新建模板</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">名称</th>
                  <th className="px-4 py-3 text-left">描述</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3 text-left">Item数</th>
                  <th className="px-4 py-3 text-left">更新时间</th>
                  <th className="px-4 py-3 text-left w-32">操作</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{t.name}</td>
                    <td className="px-4 py-3 text-gray-600">{t.description || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{t.itemCount}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(t.updatedAt)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setEditing(t)} className="text-primary hover:underline text-sm mr-3">编辑</button>
                      <button onClick={() => handleDelete(t.id)} className="text-red-500 hover:underline text-sm">删除</button>
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">暂无数据</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {total > pageSize && (
            <div className="flex justify-center gap-2 mt-6">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">上一页</button>
              <span className="px-3 py-1 text-sm text-gray-600">{page} / {Math.ceil(total / pageSize)}</span>
              <button disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50">下一页</button>
            </div>
          )}
        </>
      )}

      {showCreate && (
        <TemplateModal saving={saving} onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}

      {editing && (
        <TemplateModal template={editing} saving={saving} onSave={(data) => handleUpdate(editing.id, data)} onClose={() => setEditing(null)} />
      )}
    </div>
  )
}

const TemplateModal: FC<{
  template?: AnalyzeTemplate
  saving: boolean
  onSave: (data: CreateAnalyzeTemplateRequest) => void
  onClose: () => void
}> = ({ template, saving, onSave, onClose }) => {
  const [form, setForm] = useState({
    templateId: template?.templateId || '',
    name: template?.name || '',
    description: template?.description || '',
  })
  const [items, setItems] = useState(template?.items || [
    { role: 'item', chatTemplateId: '', propertyName: '', dependsOn: '', minInputLength: 0, displayOrder: 0 }
  ])

  const addItem = () => setItems(prev => [...prev, { role: 'item', chatTemplateId: '', propertyName: '', dependsOn: '', minInputLength: 0, displayOrder: prev.length }])
  const removeItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i))
  const updateItem = (i: number, field: string, value: any) => setItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item))

  const handleSubmit = () => {
    onSave({
      templateId: form.templateId,
      name: form.name,
      description: form.description || undefined,
      items: items.filter(i => i.chatTemplateId && i.propertyName),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-medium">{template ? '编辑模板' : '新建模板'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">模板ID {template ? '(不可修改)' : ''}</label>
              <input value={form.templateId} onChange={e => setForm(f => ({ ...f, templateId: e.target.value }))} disabled={!!template} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">分析项</label>
              <button onClick={addItem} className="text-sm text-primary hover:underline">+ 添加项</button>
            </div>
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg">
                  <select value={item.role} onChange={e => updateItem(i, 'role', e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm">
                    <option value="item">item</option>
                    <option value="preprocessor">preprocessor</option>
                  </select>
                  <input value={item.chatTemplateId} onChange={e => updateItem(i, 'chatTemplateId', e.target.value)} placeholder="chatTemplateId" className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <input value={item.propertyName} onChange={e => updateItem(i, 'propertyName', e.target.value)} placeholder="propertyName" className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <input value={item.dependsOn} onChange={e => updateItem(i, 'dependsOn', e.target.value)} placeholder="dependsOn" className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <input type="number" value={item.minInputLength} onChange={e => updateItem(i, 'minInputLength', Number(e.target.value))} className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" />
                  <button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700 text-sm px-2">删除</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">取消</button>
          <button onClick={handleSubmit} disabled={saving || !form.templateId || !form.name} className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50">
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(s: string) {
  if (!s) return '-'
  return new Date(s).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })
}
